import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Container, Card, Spinner, Alert, Form, Pagination, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { UAParser } from 'ua-parser-js';
import { PersonCircle, ClockHistory, GeoAlt, Laptop, Building } from 'react-bootstrap-icons';

// 1件のログをカード形式で表示するコンポーネント
const LogItemCard = ({ log, user, company, isExpanded, onToggle }) => {
  const parser = new UAParser(log.userAgent);
  const result = parser.getResult();

  const browser = result.browser.name ? `${result.browser.name} ${result.browser.version || ''}`.trim() : '不明';
  const os = result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : '不明';

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
        aria-expanded={isExpanded}
        className="bg-light"
      >
        <Row className="align-items-center">
          <Col md={7}>
            <div className="d-flex align-items-center">
              <PersonCircle size={32} className="me-3 text-primary flex-shrink-0" />
              <div>
                <h5 className="mb-0 fw-bold">{log.personName || '不明なユーザー'}</h5>
                <small className="text-muted">{log.email}</small>
              </div>
            </div>
          </Col>
          <Col md={5} className="text-md-end mt-2 mt-md-0">
            <div className="d-flex align-items-center justify-content-md-end text-muted">
              <ClockHistory className="me-2" />
              <small>{log.loginAt ? log.loginAt.toDate().toLocaleString('ja-JP') : 'N/A'}</small>
            </div>
          </Col>
        </Row>
      </Card.Header>

      {isExpanded && (
        <Card.Body>
          <Row className="small text-muted">
            <Col md={6} className="d-flex align-items-center mb-2 mb-md-0">
              <GeoAlt className="me-2 flex-shrink-0" />
              <span>IPアドレス: {log.ipAddress}</span>
            </Col>
            <Col md={6} className="d-flex align-items-center">
              <Laptop className="me-2 flex-shrink-0" />
              <span>利用環境: {browser} / {os}</span>
            </Col>
          </Row>
          <hr />
          <h6><Building className="me-2" />会社情報</h6>
          {company ? (
            <div className="small text-muted">
              <Row>
                <Col md={6}>
                  <strong>会社名:</strong> {company.companyPrefix}{company.companyName}{company.companySuffix}
                </Col>
                <Col md={6}>
                  <strong>支店名:</strong> {company.branchName || '登録なし'}
                </Col>
                <Col md={6}>
                  <strong>部署名:</strong> {company.departmentName || '登録なし'}
                </Col>
                <Col md={6}>
                  <strong>ユーザー属性:</strong> {user?.userAttribute || '不明'}
                </Col>
              </Row>
            </div>
          ) : (
            <p className="small text-muted">このユーザーには会社情報が紐付いていません。</p>
          )}
        </Card.Body>
      )}
    </Card>
  );
};

// ページネーションの表示を制御するコンポーネント
const CustomPagination = ({ currentPage, totalPages, paginate }) => {
  const maxPageButtons = 5;
  let startPage, endPage;

  if (totalPages <= maxPageButtons) {
    startPage = 1;
    endPage = totalPages;
  } else {
    const maxPagesBeforeCurrentPage = Math.floor(maxPageButtons / 2);
    const maxPagesAfterCurrentPage = Math.ceil(maxPageButtons / 2) - 1;
    if (currentPage <= maxPagesBeforeCurrentPage) {
      startPage = 1;
      endPage = maxPageButtons;
    } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
      startPage = totalPages - maxPageButtons + 1;
      endPage = totalPages;
    } else {
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  const pageNumbers = [...Array((endPage - startPage) + 1)].map((_, i) => startPage + i);

  return (
    <Pagination className="justify-content-center mt-3">
      <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
      <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
      {startPage > 1 && <Pagination.Ellipsis onClick={() => paginate(startPage - 1)} />}
      {pageNumbers.map(number => (
        <Pagination.Item key={number} active={number === currentPage} onClick={() => paginate(number)}>
          {number}
        </Pagination.Item>
      ))}
      {endPage < totalPages && <Pagination.Ellipsis onClick={() => paginate(endPage + 1)} />}
      <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
      <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
    </Pagination>
  );
};

function LoginLogViewer() {
  const [logs, setLogs] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const logsPerPage = 20;

  useEffect(() => {
    document.title = 'ログイン履歴 | My Order System';

    const fetchData = async () => {
      try {
        const usersQuery = getDocs(collection(db, 'users'));
        const companiesQuery = getDocs(collection(db, 'companies'));
        const logsQuery = getDocs(query(collection(db, 'login_logs'), orderBy('loginAt', 'desc')));

        const [usersSnapshot, companiesSnapshot, logsSnapshot] = await Promise.all([usersQuery, companiesQuery, logsQuery]);

        const usersDataMap = new Map();
        usersSnapshot.forEach(doc => usersDataMap.set(doc.id, doc.data()));
        setUsersMap(usersDataMap);

        const companiesDataMap = new Map();
        companiesSnapshot.forEach(doc => companiesDataMap.set(doc.id, doc.data()));
        setCompaniesMap(companiesDataMap);

        const fetchedLogs = logsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setLogs(fetchedLogs);

      } catch (err) {
        setError('ログイン履歴の取得に失敗しました。');
        console.error(err);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const filteredLogs = useMemo(() => {
    return logs.filter(log =>
      (log.personName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (log.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (log.ipAddress?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
  }, [logs, searchTerm]);

  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleToggleExpand = (logId) => {
    setExpandedLogId(prevId => (prevId === logId ? null : logId));
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">
            ログイン履歴
          </Card.Header>
          <Card.Body className="p-4">
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="氏名、メールアドレス、IPアドレスで検索..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Form.Group>

            {
              loading ?
                (
                  <div className="text-center">
                    <Spinner animation="border" />
                  </div>
                )
                :
                (
                  <>
                    <p>
                      {filteredLogs.length}件のログが見つかりました。
                    </p>

                    <div>
                      {currentLogs.map(log => {
                        const user = usersMap.get(log.uid);
                        const company = user ? companiesMap.get(user.companyId) : null;
                        return (
                          <LogItemCard
                            key={log.id}
                            log={log}
                            user={user}
                            company={company}
                            isExpanded={expandedLogId === log.id}
                            onToggle={() => handleToggleExpand(log.id)}
                          />
                        );
                      })}
                    </div>

                    {totalPages > 1 &&
                      (
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          paginate={paginate}
                        />
                      )
                    }
                  </>
                )}

            <hr className="my-4" />
            <Link to="/admin">
              管理者メニューに戻る
            </Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default LoginLogViewer;
